import React, { useEffect, useState } from 'react';
import { Loader2, FileSpreadsheet, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { oapi, odownload } from '@/lib/officialsApi';

const Result = ({ r }) => (
  <div className="rounded-xl border bg-slate-50 p-3 text-sm space-y-1" data-testid="import-result">
    <div><b className="text-emerald-700">{r.created}</b> added · <b className="text-[#00247D]">{r.updated}</b> updated · {r.total_rows} rows read</div>
    {r.errors.length > 0 && (
      <ul className="text-xs text-red-700 max-h-28 overflow-y-auto" data-testid="import-errors">
        {r.errors.map(e => <li key={e.row}>Row {e.row}: {e.error}</li>)}
      </ul>
    )}
  </div>
);

export const ImportOfficialsDialog = ({ open, orgId, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);
  const template = (fmt) => odownload('/officials/import-template', { format: fmt }, `officials_import_template.${fmt}`).catch(e => toast.error(e.message));

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await oapi('/officials/import', { method: 'POST', orgId, body: fd });
      setResult(r);
      toast.success(`${r.created} added · ${r.updated} updated`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="import-officials-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">Import officials</DialogTitle>
          <DialogDescription>Rows with an email already on your list update that official instead of adding a duplicate.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">1 · Download a template</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => template('csv')} data-testid="import-template-csv"><Download className="w-4 h-4 mr-1.5" />CSV</Button>
              <Button variant="outline" size="sm" onClick={() => template('xlsx')} data-testid="import-template-xlsx"><Download className="w-4 h-4 mr-1.5" />Excel</Button>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase text-slate-500 mb-2">2 · Upload your file</div>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl py-6 cursor-pointer hover:border-[#00247D]/50 hover:bg-[#00247D]/5 transition-colors" data-testid="import-dropzone">
              <FileSpreadsheet className="w-7 h-7 text-[#00247D]" />
              <span className="text-sm font-semibold text-slate-700">{file ? file.name : 'Choose a .csv or .xlsx file'}</span>
              <input type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
            </label>
          </div>
          {result && <Result r={result} />}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
            <Button disabled={!file || busy} onClick={submit} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="import-submit-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Import
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
